import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RegisterPageProps {
  onNavigateToLogin: () => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  'User with this email already exists': 'Un compte existe déjà avec cet email.',
  'Username, email, password and site_web are required': 'Tous les champs obligatoires doivent être remplis.',
  'Password must be at least 8 characters long': 'Le mot de passe doit contenir au moins 8 caractères.',
  'Too many requests, please try again later': 'Trop de tentatives, réessayez dans quelques minutes.',
};

const translateError = (msg: string) => ERROR_MESSAGES[msg] || msg;

const RegisterPage: React.FC<RegisterPageProps> = ({ onNavigateToLogin }) => {
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    site_web: '',
  });
  const [autoPublishSafti, setAutoPublishSafti] = useState(true);
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  // Reseaux dont Evoclick sait lire la page conseiller et publier les biens.
  // Ajouter un reseau ici et dans le cron suffit a l'ouvrir a l'inscription.
  const RESEAUX_AUTO = [
    { motif: /safti\.fr/i,       nom: 'SAFTI' },
    { motif: /bskimmobilier/i,   nom: 'BSK Immobilier' },
  ];
  const reseauAuto = RESEAUX_AUTO.find(r => r.motif.test(formData.site_web));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) { setError(''); setAccountExists(false); }
  };

  const validateForm = () => {
    if (!formData.username || !formData.email || !formData.password || !formData.site_web) {
      return 'Tous les champs sont obligatoires';
    }
    if (formData.password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Format d\'email invalide';
    }
    return null;
  };

  const showError = (msg: string, isExisting = false) => {
    setError(translateError(msg));
    setAccountExists(isExisting);
    setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setAccountExists(false);
    const validationError = validateForm();
    if (validationError) { showError(validationError); return; }
    try {
      // La plupart des gens tapent une adresse sans le protocole : on le complète
      // plutôt que de refuser une inscription par ailleurs valide.
      const site = formData.site_web.trim();
      const payload: any = { ...formData, site_web: /^https?:\/\//i.test(site) ? site : `https://${site}` };
      if (reseauAuto) payload.preferences = { auto_publish_safti: autoPublishSafti };
      await register(payload);
    } catch (err: any) {
      const msg = err.message || 'Erreur lors de l\'inscription';
      showError(msg, msg.includes('already exists'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-7 h-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Evoclick</h1>
          <p className="text-gray-500 mt-1 text-sm">30 jours offerts, sans carte bancaire</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl px-8 py-8">
          <form className="space-y-5" onSubmit={handleSubmit}>

            {error && (
              <div ref={errorRef} className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
                {accountExists && (
                  <button
                    type="button"
                    onClick={onNavigateToLogin}
                    className="mt-2 text-sm text-red-700 underline font-semibold"
                  >
                    Se connecter à la place →
                  </button>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom complet</label>
              <input
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                placeholder="Jean Dupont"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                placeholder="jean@example.fr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mot de passe</label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Minimum 8 caractères</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre page conseiller ou votre site</label>
              <input
                name="site_web"
                type="text"
                value={formData.site_web}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                placeholder="https://www.safti.fr/agent/... ou https://bskimmobilier.com/..."
              />
            </div>

            {reseauAuto && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🏠</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Vous êtes conseiller {reseauAuto.nom}</p>
                    <p className="text-xs text-amber-700 mt-0.5">Evoclick peut diffuser automatiquement vos nouveaux biens sur vos réseaux sociaux dès qu'ils sont publiés sur {reseauAuto.nom}.</p>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoPublishSafti}
                        onChange={e => setAutoPublishSafti(e.target.checked)}
                        className="w-4 h-4 accent-amber-600 rounded"
                      />
                      <span className="text-sm font-medium text-amber-800">Diffuser mes biens sur mes réseaux automatiquement</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Création...
                </span>
              ) : (
                'Créer mon compte →'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-sm text-gray-400">Déjà un compte ?</span>
            <button
              onClick={onNavigateToLogin}
              className="ml-2 text-sm font-medium text-violet-600 hover:text-violet-700"
            >
              Se connecter
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          En créant un compte, vous acceptez nos conditions d'utilisation.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
